import { Button, Flex, Text } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { EidEasySigningMethod } from '@dealstack/shared/lib/common/eideasy/signingMethods';
import { METHOD_OPTIONS } from './MethodSelectStep.types';
import { StepBody } from './StepBody';
import { StepFooter } from './StepFooter';
import { StepHeading } from './typography';

interface Props {
	error: string | null;
	selectedMethod?: EidEasySigningMethod | null;
	onTryAgain: () => void;
}

/**
 * Per-error overrides for cases where "Something went wrong" misdescribes what happened.
 * Fallbacks are legacy: new entries omit them so a missing Tolgee key shows as the raw key.
 */
const ERROR_COPY: Record<
	string,
	{ titleKey: string; titleFallback?: string; bodyFallback?: string }
> = {
	'eid_easy_signing.error_document_changed': {
		titleKey: 'eid_easy_signing.document_changed_title',
		titleFallback: 'This document has changed',
		bodyFallback:
			'Another signer completed their signature while you were signing, so yours was not applied. Try again to sign the latest version.',
	},
	'eid_easy_signing.error_witness_required_bulk': {
		titleKey: 'eid_easy_signing.witness_required_bulk_title',
	},
	'eid_easy_signing.error_document_signing_failed': {
		titleKey: 'eid_easy_signing.document_signing_failed_title',
	},
};

export const ErrorStep = ({ error, selectedMethod, onTryAgain }: Props) => {
	const tCommonUi = useTranslationCommonUi();
	const methodLabelKey = METHOD_OPTIONS.find(
		(option) => option.method === selectedMethod
	)?.labelKey;
	const copy = error ? ERROR_COPY[error] : undefined;

	return (
		<Flex direction="column" w="100%" h="100%">
			<StepBody>
				<Flex direction="column" align="center" gap="md" style={{ flex: 1 }}>
					<StepHeading>
						{copy
							? tCommonUi(copy.titleKey, { defaultValue: copy.titleFallback })
							: tCommonUi('eid_easy_signing.something_went_wrong')}
					</StepHeading>
					<Text c="red" ta="center">
						{error
							? tCommonUi(error, { defaultValue: copy?.bodyFallback })
							: tCommonUi('eid_easy_signing.error_generic')}
					</Text>
					{methodLabelKey && (
						<Text size="sm" c="dimmed" ta="center">
							{tCommonUi('eid_easy_signing.error_method_label', {
								method: tCommonUi(methodLabelKey),
							})}
						</Text>
					)}
					<Icon variant="warning" size="3xl" />
				</Flex>
			</StepBody>
			<StepFooter>
				<Button variant="filled" fullWidth onClick={onTryAgain}>
					{tCommonUi('eid_easy_signing.try_again')}
				</Button>
			</StepFooter>
		</Flex>
	);
};
